/**
 =========================================================
 * AutoCount — AI Inventory Assistant
 =========================================================
 *
 * Ask the live inventory questions in plain English. The widget calls the
 * server-side assistant endpoint (POST /api/v1/assistant), which grounds
 * Claude in the live Prisma data. Read-only — it never mutates anything.
 */

import { useEffect, useRef, useState } from "react";

// @mui material components
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from "context";

// API client (adds base URL + bearer token)
import { apiFetch } from "api/client";

interface AIChatAssistantProps {
  open?: boolean;
  onClose?: () => void;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  isError?: boolean;
}

const GREETING: ChatMessage = {
  role: "assistant",
  content:
    "Hi! I'm your AutoCount inventory assistant. Ask me anything about the live stock — parts, fitment, suppliers, shipments, or value.",
};

const STARTERS = [
  "What needs reordering?",
  "What's low on brakes?",
  "Total inventory value?",
  "What's my margin on the brake rotor?",
];

const NAVY = "#1a2035";
const NAVY_LIGHT = "#232b46";
const ACCENT = "#49a3f1";

function AIChatAssistant({ open = false, onClose }: AIChatAssistantProps): JSX.Element {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [input, setInput] = useState<string>("");
  const [messages, setMessages] = useState<ChatMessage[]>([GREETING]);
  const [typing, setTyping] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing, open]);

  const send = async (text: string) => {
    const question = text.trim();
    if (!question || typing) return;

    const next: ChatMessage[] = [...messages, { role: "user", content: question }];
    setMessages(next);
    setInput("");
    setTyping(true);

    try {
      const payload = {
        messages: next
          .filter((m) => !m.isError)
          .slice(-12)
          .map((m) => ({ role: m.role, content: m.content })),
      };
      const data = await apiFetch<{ reply: string }>("/assistant", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          isError: true,
          content:
            err?.message && err?.statusCode === 429
              ? "I'm getting a lot of questions right now — give me a minute and try again."
              : "I couldn't reach the inventory brain just now — please try again in a moment.",
        },
      ]);
    } finally {
      setTyping(false);
    }
  };

  const handleNewChat = () => {
    setMessages([GREETING]);
    setInput("");
  };

  const showStarters = messages.length <= 1 && !typing;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      hideBackdrop
      disableEnforceFocus
      PaperProps={{
        sx: {
          position: "fixed",
          bottom: { xs: 8, sm: 24 },
          right: { xs: 8, sm: 24 },
          m: 0,
          width: { xs: "calc(100% - 16px)", sm: "400px" },
          height: { xs: "80vh", sm: "580px" },
          maxHeight: "calc(100vh - 32px)",
          borderRadius: "14px",
          overflow: "hidden",
          boxShadow: "0 18px 50px rgba(0,0,0,0.45)",
          bgcolor: darkMode ? NAVY : "#ffffff",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <MDBox
        px={2}
        py={1.5}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`,
          borderBottom: `1px solid rgba(255,255,255,0.08)`,
          flexShrink: 0,
        }}
      >
        <MDBox display="flex" alignItems="center">
          <Avatar
            sx={{
              width: 38,
              height: 38,
              mr: 1.5,
              background: `linear-gradient(135deg, ${ACCENT} 0%, #1A73E8 100%)`,
            }}
          >
            <Icon sx={{ color: "#fff" }}>bolt</Icon>
          </Avatar>
          <MDBox lineHeight={1.1}>
            <MDTypography variant="button" fontWeight="bold" sx={{ color: "#fff" }}>
              Inventory Assistant
            </MDTypography>
            <MDBox display="flex" alignItems="center">
              <MDBox
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: "#4CAF50",
                  mr: 0.6,
                  boxShadow: "0 0 6px rgba(76,175,80,0.9)",
                }}
              />
              <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                AI · grounded in live stock data
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
        <MDBox display="flex" alignItems="center">
          <Tooltip title="New chat">
            <IconButton size="small" onClick={handleNewChat} sx={{ color: "rgba(255,255,255,0.7)" }}>
              <Icon fontSize="small">refresh</Icon>
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.7)", ml: 0.5 }}>
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </MDBox>
      </MDBox>

      {/* Messages */}
      <MDBox
        ref={scrollRef}
        px={1.75}
        py={1.5}
        sx={{
          flex: 1,
          overflowY: "auto",
          bgcolor: darkMode ? NAVY : "#f6f8fb",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: darkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
            borderRadius: 3,
          },
        }}
      >
        {messages.map((msg, i) => (
          <MDBox
            key={i}
            display="flex"
            justifyContent={msg.role === "user" ? "flex-end" : "flex-start"}
            mb={1.25}
          >
            {msg.role === "assistant" && (
              <Avatar
                sx={{
                  width: 26,
                  height: 26,
                  mr: 1,
                  mt: 0.3,
                  background: msg.isError
                    ? "linear-gradient(135deg,#e53935,#b71c1c)"
                    : `linear-gradient(135deg, ${ACCENT} 0%, #1A73E8 100%)`,
                }}
              >
                <Icon sx={{ fontSize: "0.9rem !important", color: "#fff" }}>
                  {msg.isError ? "error_outline" : "bolt"}
                </Icon>
              </Avatar>
            )}
            <MDBox
              px={1.5}
              py={1}
              sx={{
                maxWidth: "82%",
                borderRadius:
                  msg.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                background:
                  msg.role === "user"
                    ? "linear-gradient(135deg, #1A73E8 0%, #49a3f1 100%)"
                    : darkMode
                    ? NAVY_LIGHT
                    : "#ffffff",
                border:
                  msg.role === "user"
                    ? "none"
                    : `1px solid ${darkMode ? "rgba(255,255,255,0.08)" : "rgba(26,32,53,0.08)"}`,
                boxShadow:
                  msg.role === "user"
                    ? "0 4px 12px rgba(26,115,232,0.35)"
                    : "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <MDTypography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontSize: "0.86rem",
                  lineHeight: 1.5,
                  color:
                    msg.role === "user" ? "#fff" : darkMode ? "rgba(255,255,255,0.9)" : NAVY,
                }}
              >
                {msg.content}
              </MDTypography>
            </MDBox>
          </MDBox>
        ))}

        {typing && (
          <MDBox display="flex" alignItems="center" mb={1}>
            <Avatar
              sx={{
                width: 26,
                height: 26,
                mr: 1,
                background: `linear-gradient(135deg, ${ACCENT} 0%, #1A73E8 100%)`,
              }}
            >
              <Icon sx={{ fontSize: "0.9rem !important", color: "#fff" }}>bolt</Icon>
            </Avatar>
            <MDBox
              px={1.75}
              py={1.1}
              sx={{
                borderRadius: "14px 14px 14px 4px",
                background: darkMode ? NAVY_LIGHT : "#ffffff",
                border: `1px solid ${darkMode ? "rgba(255,255,255,0.08)" : "rgba(26,32,53,0.08)"}`,
                display: "flex",
                gap: "5px",
                "@keyframes acPulse": {
                  "0%, 80%, 100%": { opacity: 0.25, transform: "scale(0.85)" },
                  "40%": { opacity: 1, transform: "scale(1)" },
                },
              }}
            >
              {[0, 1, 2].map((d) => (
                <MDBox
                  key={d}
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: ACCENT,
                    animation: "acPulse 1.2s infinite ease-in-out",
                    animationDelay: `${d * 0.18}s`,
                  }}
                />
              ))}
            </MDBox>
          </MDBox>
        )}

        {showStarters && (
          <MDBox mt={1}>
            <MDTypography
              variant="caption"
              sx={{
                color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(26,32,53,0.55)",
                display: "block",
                mb: 0.75,
                ml: 0.25,
              }}
            >
              Try asking:
            </MDTypography>
            <MDBox display="flex" flexWrap="wrap" gap={0.75}>
              {STARTERS.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  size="small"
                  onClick={() => send(s)}
                  sx={{
                    cursor: "pointer",
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: darkMode ? "#fff" : NAVY,
                    bgcolor: darkMode ? "rgba(73,163,241,0.15)" : "rgba(26,115,232,0.08)",
                    border: `1px solid ${darkMode ? "rgba(73,163,241,0.4)" : "rgba(26,115,232,0.25)"}`,
                    "&:hover": {
                      bgcolor: darkMode ? "rgba(73,163,241,0.3)" : "rgba(26,115,232,0.16)",
                    },
                  }}
                />
              ))}
            </MDBox>
          </MDBox>
        )}
      </MDBox>

      {/* Input */}
      <MDBox
        px={1.5}
        py={1.25}
        sx={{
          bgcolor: darkMode ? NAVY : "#ffffff",
          borderTop: `1px solid ${darkMode ? "rgba(255,255,255,0.08)" : "rgba(26,32,53,0.08)"}`,
          flexShrink: 0,
        }}
      >
        <MDBox display="flex" alignItems="center">
          <TextField
            fullWidth
            placeholder="Ask about parts, stock, suppliers…"
            variant="outlined"
            size="small"
            value={input}
            disabled={typing}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            inputProps={{ maxLength: 400 }}
            sx={{
              mr: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                color: darkMode ? "#fff" : NAVY,
                bgcolor: darkMode ? NAVY_LIGHT : "#f6f8fb",
                "& fieldset": {
                  borderColor: darkMode ? "rgba(255,255,255,0.15)" : "rgba(26,32,53,0.12)",
                },
                "&:hover fieldset": { borderColor: ACCENT },
                "&.Mui-focused fieldset": { borderColor: ACCENT },
              },
              "& .MuiInputBase-input::placeholder": {
                color: darkMode ? "rgba(255,255,255,0.4)" : "rgba(26,32,53,0.4)",
                opacity: 1,
              },
            }}
          />
          <IconButton
            onClick={() => send(input)}
            disabled={!input.trim() || typing}
            sx={{
              width: 40,
              height: 40,
              background: `linear-gradient(135deg, #1A73E8 0%, ${ACCENT} 100%)`,
              color: "#fff !important",
              "&:hover": { background: "#1A73E8" },
              "&.Mui-disabled": {
                background: darkMode ? "rgba(255,255,255,0.08)" : "rgba(26,32,53,0.08)",
                color: darkMode ? "rgba(255,255,255,0.3) !important" : "rgba(26,32,53,0.3) !important",
              },
            }}
          >
            {typing ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : (
              <Icon fontSize="small">send</Icon>
            )}
          </IconButton>
        </MDBox>
        <MDTypography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            mt: 0.75,
            fontSize: "0.65rem",
            color: darkMode ? "rgba(255,255,255,0.35)" : "rgba(26,32,53,0.4)",
          }}
        >
          Read-only assistant · answers come from live inventory data
        </MDTypography>
      </MDBox>
    </Dialog>
  );
}

export default AIChatAssistant;
